import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Drawer, Flex, Loader } from '@mantine/core';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import {
	drawerTransitionProps,
	groupedSelectDividerStyles,
} from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { TAX_INFO_FORM_FIELDS } from './utils/types.ts';
import { Select, TextInput } from 'react-hook-form-mantine';
import { useTaxInfoDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/TaxInfoDrawer/hooks/useTaxInfoDrawer.ts';

import { COUNTRIES_NAME_CODE_GROUPED_OPTIONS } from '@dealstack/shared/lib/countries/countriesData.ts';
import { GetPersonTaxInformationRes } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { makeOptionWithTestId } from '@dealstack/frontend/shared/components/Select/withOptionTestId';

const CountryOption = makeOptionWithTestId(TEST_ELEMENT_IDS.CAP_COUNTRY_OPTION);

interface Props {
	hasPermission: boolean;
	opened: boolean;
	onClose: () => void;
	taxData: GetPersonTaxInformationRes | null;
}

export const TaxInfoDrawer = ({ taxData, hasPermission, opened, onClose }: Props) => {
	const tCapManage = useTranslationCapManage();
	const [showResetTinButton, setShowResetTinButton] = useState<boolean>(false);

	const {
		taxInfoForm,
		onFormSubmit,
		isLoading,
		onTinLookup,
		tinLookupData,
		tinLookupLoading,
		manualTinMode,
		setManualTinMode,
		resetFormValues,
		taxNumber,
		country,
	} = useTaxInfoDrawer(onClose);

	const toggleManualMode = () => {
		if (manualTinMode) {
			taxInfoForm.setValue(
				TAX_INFO_FORM_FIELDS.authority,
				tinLookupData?.tinType || taxData?.authority || undefined
			);
		}

		setManualTinMode(!manualTinMode);
	};

	const resetForm = () => {
		resetFormValues();
		setShowResetTinButton(false);
	};

	const disableSubmitButton = manualTinMode ? false : !tinLookupData?.isValid;

	useEffect(() => {
		if (taxData) {
			taxInfoForm.reset({
				[TAX_INFO_FORM_FIELDS.country]: taxData.countryCode,
				[TAX_INFO_FORM_FIELDS.authority]: taxData.authority ?? undefined,
				[TAX_INFO_FORM_FIELDS.number]: taxData.number ?? undefined,
			});
			setShowResetTinButton(true);
		} else {
			resetFormValues();
			setManualTinMode(false);
			setShowResetTinButton(false);
		}
	}, [taxData, opened]);

	const disableTinField = useMemo(() => {
		if (!hasPermission) {
			return true;
		}

		if (manualTinMode) {
			return false;
		}

		return tinLookupLoading && tinLookupData?.isValid;
	}, [tinLookupData, manualTinMode, hasPermission]);

	useEffect(() => {
		setShowResetTinButton(!!(tinLookupData && tinLookupData?.isValid));
	}, [tinLookupData]);

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={
				<span data-testid={TEST_ELEMENT_IDS.CAP_TAX_INFO_DRAWER_HEADING}>
					{taxData
						? tCapManage('tax_info.drawer.edit_title')
						: tCapManage('tax_info.drawer.add_title')}
				</span>
			}
		>
			<FormProvider {...taxInfoForm}>
				<Flex direction="column" justify="space-between" h="100%">
					<Flex direction="column" gap="1rem">
						<Select
							name={TAX_INFO_FORM_FIELDS.country}
							control={taxInfoForm.control}
							label={tCapManage('tax_info.drawer.country.label')}
							placeholder={tCapManage('tax_info.drawer.country.placeholder')}
							size="md"
							data={COUNTRIES_NAME_CODE_GROUPED_OPTIONS}
							searchable
							disabled={!hasPermission}
							data-testid={TEST_ELEMENT_IDS.CAP_TAX_INFO_FORM_COUNTRY_SELECT}
							renderOption={CountryOption}
							styles={groupedSelectDividerStyles}
						/>

						<TextInput
							control={taxInfoForm.control}
							name={TAX_INFO_FORM_FIELDS.number}
							label={tCapManage('tax_info.drawer.tin.label')}
							placeholder={tCapManage('tax_info.drawer.tin.placeholder')}
							size="md"
							disabled={!hasPermission || disableTinField}
							data-testid={TEST_ELEMENT_IDS.CAP_TAX_INFO_FORM_NUMBER_INPUT}
						/>

						<TextInput
							control={taxInfoForm.control}
							name={TAX_INFO_FORM_FIELDS.authority}
							label={tCapManage('tax_info.drawer.type.label')}
							placeholder={tCapManage('tax_info.drawer.type.placeholder')}
							size="md"
							disabled={
								!hasPermission ||
								!manualTinMode ||
								(tinLookupData?.isValid && !manualTinMode)
							}
							data-testid={TEST_ELEMENT_IDS.CAP_TAX_INFO_FORM_NUMBER_TYPE_INPUT}
						/>
						<Flex align="center" justify="space-between" gap="0.5rem">
							<Flex align="center" gap="0.5rem" wrap="wrap">
								{manualTinMode ? null : (
									<>
										{taxData &&
											(taxData?.number !== taxNumber ||
												taxData?.countryCode !== country) &&
											showResetTinButton && (
												<Button
													variant="outline"
													onClick={onTinLookup}
													disabled={
														tinLookupLoading ||
														taxNumber === tinLookupData?.tin
													}
												>
													{tCapManage(
														'tax_info.drawer.tin_lookup_button'
													)}
												</Button>
											)}
										{showResetTinButton ? (
											<Button variant="outline" onClick={resetForm}>
												{tCapManage('tax_info.drawer.tin_reset_button')}
											</Button>
										) : (
											<Button
												variant="outline"
												onClick={onTinLookup}
												disabled={tinLookupLoading || manualTinMode}
											>
												{tCapManage('tax_info.drawer.tin_lookup_button')}
											</Button>
										)}
									</>
								)}
								<Button
									variant="transparent"
									c={manualTinMode ? 'blue.6' : 'dark.8'}
									onClick={toggleManualMode}
									disabled={tinLookupLoading}
									data-testid={
										TEST_ELEMENT_IDS.CAP_TAX_INFO_FORM_MANUAL_MODE_BUTTON
									}
								>
									{manualTinMode
										? tCapManage('tax_info.drawer.tin_exit_manual_button')
										: tCapManage('tax_info.drawer.tin_enter_manual_button')}
								</Button>
							</Flex>

							{tinLookupLoading && <Loader variant="oval" size="xs" />}
						</Flex>
					</Flex>

					<Button
						onClick={taxInfoForm.handleSubmit((formValues) =>
							onFormSubmit(formValues, taxData?.id)
						)}
						disabled={isLoading || disableSubmitButton}
						data-testid={TEST_ELEMENT_IDS.CAP_TAX_INFO_FORM_SUBMIT_BUTTON}
					>
						{taxData
							? tCapManage('tax_info.drawer.edit_button')
							: tCapManage('tax_info.drawer.add_button')}
					</Button>
				</Flex>
			</FormProvider>
		</Drawer>
	);
};
